import {detectLevenshteinDistance} from '@/ts/utils/pure-functions';

function lengthAwareLevenshtein(query: string, target: string, baseDistance: number): number {
  if (baseDistance <= 1) {
    return baseDistance;
  }
  const qLen = query?.length ?? 0;
  const tLen = target?.length ?? 0;
  if (qLen > tLen) {
    return Math.min(baseDistance, detectLevenshteinDistance(query?.slice(0, tLen) ?? '', target));
  }
  if (qLen < tLen) {
    return Math.min(baseDistance, detectLevenshteinDistance(query, target?.slice(0, qLen) ?? ''));
  }
  return baseDistance;
}

/** Configuration options for fuzzy string matching behavior */
export interface FuzzyMatchOptions {
  /** Maximum distance threshold for results (default: 3) */
  maxDistance?: number;
  /** Maximum number of results to return (default: 5) */
  maxResults?: number;
  /** Whether to always run expensive Levenshtein calculation (default: false) */
  alwaysUseFuzzy?: boolean;
  /** Minimum number of exact/partial matches before running fuzzy matching (default: 5) */
  fuzzyThreshold?: number;
}

/** Result of a fuzzy match containing the original item, its extracted text, and similarity distance */
export interface FuzzyMatchResult<T> {
  item: T;
  text: string;
  distance: number;
}

/**
 * Calculates fuzzy distance between query and target strings using a tiered approach:
 * - Exact match: 0
 * - Starts with query: 0.1
 * - Contains query: 0.2
 * - Query contains target: 0.3
 * - Levenshtein distance with length optimization: varies
 */
export function calculateFuzzyDistance(query: string, target: string): number {
  const queryLower = (query?.toLowerCase() ?? '').trim();
  const targetLower = target?.toLowerCase() ?? '';
  const targetStartsWithQuery = Boolean(targetLower?.startsWith(queryLower));
  const targetIncludesQuery = Boolean(targetLower?.includes(queryLower));
  const queryIncludesTarget = Boolean(queryLower?.includes(targetLower));

  if (queryLower === targetLower) {
    return 0;
  } else if (targetStartsWithQuery) {
    return 0.1;
  } else if (targetIncludesQuery) {
    return 0.2;
  } else if (queryIncludesTarget) {
    return 0.3;
  }
  const distance = detectLevenshteinDistance(queryLower, targetLower);
  return lengthAwareLevenshtein(queryLower, targetLower, distance);
}

/**
 * Performs fuzzy matching on an array of items with configurable options.
 * Returns items ranked by similarity to the query string.
 */
export function fuzzyMatchItems<T>(
  items: T[],
  query: string,
  textExtractor: (item: T) => string,
  options: FuzzyMatchOptions = {},
): FuzzyMatchResult<T>[] {
  const {
    maxDistance = 3,
    maxResults = 5,
    alwaysUseFuzzy = false,
    fuzzyThreshold = 5,
  } = options;

  const queryInput = (query?.toLowerCase() ?? '').trim();

  // Show recent items when query is too short
  if ((queryInput?.length ?? 0) < 2) {
    return items.slice(0, maxResults).map(item => ({
      item,
      text: textExtractor(item),
      distance: 0,
    }));
  }

  // First pass: calculate distances for all items
  const ratedItems = items.map(item => {
    const text = textExtractor(item);
    const distance = calculateFuzzyDistance(queryInput, text);
    return {item, text, distance};
  });

  // Determine if we should run expensive fuzzy matching for high-distance items
  const lowDistanceItems = ratedItems.filter(item => item.distance < 1);
  const shouldRunFuzzy = alwaysUseFuzzy || lowDistanceItems.length <= fuzzyThreshold;

  let filteredItems = ratedItems;
  
  if (shouldRunFuzzy) {
    // Include fuzzy matches up to the distance threshold
    filteredItems = ratedItems.filter(item => item.distance < maxDistance);
  } else {
    // Skip expensive processing and only return good matches
    filteredItems = ratedItems.filter(item => item.distance < 1);
  }

  return filteredItems
    .sort((a, b) => a.distance - b.distance)
    .slice(0, maxResults);
}
